import { ProbabilityRange } from './types';
import type { MaterialPrediction } from '../../api/queries/materialClassification';
export interface ConfidenceRanges {
  high: [number, number];
  medium: [number, number];
  low: [number, number];
}

export const DEFAULT_CONFIDENCE_RANGES: ConfidenceRanges = {
  high: [0.9, 1],
  medium: [0.5, 0.9],
  low: [0.0, 0.5],
};

export function getConfidence(probability: ProbabilityRange) {
  const ranges = DEFAULT_CONFIDENCE_RANGES;
  switch (probability) {
    case 'high':
      return ranges.high[0];
    case 'medium':
      return ranges.medium[0];
    default:
      return 0;
  }
}

export function getConfidenceLevel(probability: number): ProbabilityRange {
  const ranges = DEFAULT_CONFIDENCE_RANGES;
  if (probability >= ranges.high[0]) return 'high';
  if (probability >= ranges.medium[0]) return 'medium';
  return 'low';
}

export const CONFIDENCE_COLORS = {
  high: 'rgb(76, 175, 80)',
  medium: 'rgb(255, 193, 7)',
  low: 'rgb(158, 158, 158)',
};

export function getProbabilityColor(probability: ProbabilityRange | undefined): string {
  if (!probability) return CONFIDENCE_COLORS.low;
  return CONFIDENCE_COLORS[probability];
}

export function getConfidenceRangeDisplay(): { high: string; medium: string; low: string } {
  return {
    high: `≥${Math.round(DEFAULT_CONFIDENCE_RANGES.high[0] * 100)}%`,
    medium: `${Math.round(DEFAULT_CONFIDENCE_RANGES.medium[0] * 100)}% - ${Math.round(DEFAULT_CONFIDENCE_RANGES.medium[1] * 100)}%`,
    low: `<${Math.round(DEFAULT_CONFIDENCE_RANGES.medium[0] * 100)}%`,
  };
}

const EDGE_ESTIMATION_BLACKLISTED_CLASSES = new Set([
  'bags',
  'covered',
  'empty',
  'soil',
  'track_ballast',
  'gravel',
  'truck',
  'car',
  'loader',
]);

export function shouldDisplayEdgeEstimation(predictions: MaterialPrediction[] | undefined): boolean {
  if (!predictions || predictions.length === 0) return false;
  const [top1, top2] = predictions;
  const lowConfidenceLimit = DEFAULT_CONFIDENCE_RANGES.medium[0];
  if (top1.probability >= lowConfidenceLimit) {
    return !EDGE_ESTIMATION_BLACKLISTED_CLASSES.has(top1.className);
  }
  const isTop1Allowed = !EDGE_ESTIMATION_BLACKLISTED_CLASSES.has(top1.className);
  const isTop2Allowed = top2 !== undefined && !EDGE_ESTIMATION_BLACKLISTED_CLASSES.has(top2.className);
  return isTop1Allowed || isTop2Allowed;
}

const SOIL_CLASSIFICATION_MATERIAL_CLASSES = new Set(['soil']);

export function shouldDisplaySoilClassification(predictions: MaterialPrediction[] | undefined): boolean {
  if (!predictions || predictions.length === 0) return false;
  const [top1, top2] = predictions;
  const lowConfidenceLimit = DEFAULT_CONFIDENCE_RANGES.medium[0];
  if (top1.probability >= lowConfidenceLimit) {
    return SOIL_CLASSIFICATION_MATERIAL_CLASSES.has(top1.className);
  }

  const isTop1Soil = SOIL_CLASSIFICATION_MATERIAL_CLASSES.has(top1.className);
  const isTop2Soil = top2 !== undefined && SOIL_CLASSIFICATION_MATERIAL_CLASSES.has(top2.className);
  return isTop1Soil || isTop2Soil;
}
